import { InformationCircleIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Label } from "@/atoms-react/text/Text.react";
import { companyDetails } from "@/features/company/types";
import { LegalDocument } from "@/features/user/document/types";
import { UploadDocumentInput } from "@/molecules/upload-document-input";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { formStyle } from "../form-style";
import { UploadCompanyDocumentVerified } from "./types";

type DocumentUploadProInputProps = {
  companyDetails: companyDetails;
  uploadDocumentData: UploadCompanyDocumentVerified;
  tooltipContent: string;
  isAdmin: boolean;
  actionEnabled: boolean | undefined;
};

export const ProVerifiedDocumentUpload = ({
  companyDetails,
  uploadDocumentData,
  tooltipContent,
  isAdmin = false,
  actionEnabled
}: DocumentUploadProInputProps) => {
  const translation = useTranslation(["pass", "error", "atoms"]);
  const { t } = translation;

  const handleDocument = (identityDocument: LegalDocument | null) => {
    if (identityDocument && identityDocument.name && identityDocument.base64docs) {
      return identityDocument;
    }
    return null;
  };

  return (
    <div className={formStyle.containerLine}>
      <div className={formStyle.containerLineTitle}>
        <Label>{t("business_links.add_link.input.document_company")}</Label>
        <div className={formStyle.tooltipContainer}>
          <InformationCircleIcon
            className={formStyle.tooltipContainerIcon}
            data-tooltip={tooltipContent}
            data-position="right"
          />
        </div>
      </div>
      <div className={formStyle.containerLineContent}>
        <UploadDocumentInput
          dataCy="pro-verified-form-document-upload"
          className="w-full"
          document={handleDocument(companyDetails.legalEntity.IdentityDocument)}
          disabled={
            (!!companyDetails.legalEntity.UserValidation || actionEnabled === false) && !isAdmin
          }
          onError={(messagekey: string) => {
            toast.error(t(messagekey));
          }}
          onChange={async (data) => {
            if (!data.base64docs || !data.fileFormat) {
              throw new Error("No document or type provided");
            }

            const base64WithFormat = `data:${data.fileFormat};base64,${data.base64docs}`;

            try {
              await uploadDocumentData({
                legalEntityUuid: companyDetails.legalEntity.UUID,
                name: data.name,
                base64docs: base64WithFormat
              });
              toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
            } catch (e: unknown) {
              toast.error(extractErrorDescription(e, translation));
              throw new Error("Error during fetch"); // it will avoid state change TODO : toast should appear in components
            }
          }}
        />
      </div>
    </div>
  );
};
